import React from "react";
import { Stack, Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { AlertSeverity } from "../../api/alerts";

/**
 * Severity presentation, shared by the Alerts page and the notification
 * center so the two can never drift apart: a red warning icon for high, a
 * yellow caution triangle for moderate, with the level spelled out beside it
 * wherever there is room — color alone is not a status anyone can read.
 *
 * Only the icon is colored. The amber is set explicitly rather than taken
 * from `palette.warning` (MUI's default there is orange), and it is far too
 * light to put text in, so text stays at normal color.
 */
export const SEVERITY_META: Record<
  AlertSeverity,
  { label: string; color: string; Icon: typeof ErrorIcon }
> = {
  high: { label: "High", color: "#D32F2F", Icon: ErrorIcon },
  moderate: { label: "Moderate", color: "#F9A825", Icon: WarningAmberIcon },
};

/** Icon alone, for tight spots like a notification row. */
export const SeverityIcon: React.FC<{
  severity: AlertSeverity;
  fontSize?: "small" | "inherit" | "medium" | "large";
}> = ({ severity, fontSize = "small" }) => {
  // An unrecognised level from a newer server must not crash the page.
  const meta = SEVERITY_META[severity];
  if (!meta) return null;
  const { Icon } = meta;
  return (
    <Icon
      fontSize={fontSize}
      sx={{ color: meta.color }}
      titleAccess={`${meta.label} severity`}
    />
  );
};

/** Icon plus the level in words. */
export const SeverityMarker: React.FC<{ severity: AlertSeverity }> = ({
  severity,
}) => {
  const meta = SEVERITY_META[severity];
  if (!meta) return <Typography variant="body2">{severity}</Typography>;
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <SeverityIcon severity={severity} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {meta.label}
      </Typography>
    </Stack>
  );
};
